/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled from "styled-components";
import { useEffect, useRef } from "react";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastMessage } from "reducers/ToastMessagesReducer";
import { Toast } from "@spectrum-web-components/toast";
import { removeToast } from "actions/ToastMessageActions";
import { devices } from "./Styles";

const ToastContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  min-width: 800px;
  max-width: 800px;
  ${devices.small} {
    min-width: calc(100vw - 48px);
    max-width: calc(100vw - 48px);
  }
  width: auto;
  --mod-toast-max-inline-size: none;
  background: transparent;
  position: fixed;
  top: 88px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
`;

const ToastMessageWrapper: React.FC<{ toastMessage: ToastMessage }> = ({
  toastMessage,
}) => {
  const dispatch = useDispatch();
  const ref = useRef<Toast>(null);

  useEffect(() => {
    const closeHandler = (): void => {
      dispatch(removeToast(toastMessage.id));
    };

    const { current } = ref;
    current?.addEventListener("close", closeHandler);

    return (): void => {
      current?.removeEventListener("close", closeHandler);
    };
  }, [dispatch, ref, toastMessage.id]);

  return (
    <sp-toast ref={ref} open variant={toastMessage.variant}>
      {toastMessage.message}
    </sp-toast>
  );
};

export const ToastMessages: React.FC = () => {
  const toastMessages = useSelector((state) => state.toastMessages);

  return (
    <ToastContainer role="region">
      {toastMessages.map((toastMessage) => {
        return (
          <ToastMessageWrapper
            toastMessage={toastMessage}
            key={toastMessage.id}
          />
        );
      })}
    </ToastContainer>
  );
};
